/** Werkstatt-Shop — marketing teaser pro merch.
 *  Není funkční (žádné nákupy / ceny), prostě výloha s "Demnächst verfügbar".
 *  Fotky se hledají v /shop/<slug>.jpg, fallback je inline SVG placeholder. */

import { useMemo, useState } from "react";
import { Ic } from "@/components/ui/icons";
import { Modal } from "@/components/ui/modal";
import { t } from "@/i18n/de";

type Category =
  | "caps"
  | "shirts"
  | "hoodies"
  | "jacken"
  | "muetzen"
  | "werkzeug"
  | "brillen"
  | "notes"
  | "stifte";

type PlaceholderKind =
  | "ruler"
  | "glasses"
  | "notebook"
  | "pencils"
  | "cap"
  | "jacket"
  | "hoodie"
  | "tshirt"
  | "beanie";

interface Product {
  slug: string;
  category: Category;
  eyebrow: string;
  title: string;
  subtitle: string;
  badge?: string;
  // Pokud /shop/<slug>.jpg neexistuje, ukáže se inline SVG placeholder.
  placeholderKind: PlaceholderKind;
}

const PRODUCTS: Product[] = [
  {
    slug: "cap-classic",
    category: "caps",
    eyebrow: "Caps",
    title: "Cap · Classic Black",
    subtitle: "6-Panel · gesticktes Logo · gold auf schwarz",
    badge: "Neu",
    placeholderKind: "cap",
  },
  {
    slug: "cap-two-tone",
    category: "caps",
    eyebrow: "Caps",
    title: "Cap · Two-Tone",
    subtitle: "Schwarz / Gold · Power-Icon vorne · Snapback",
    placeholderKind: "cap",
  },
  {
    slug: "tshirt-heat-power",
    category: "shirts",
    eyebrow: "Shirts",
    title: "T-Shirt · Heat & Power · Gelb",
    subtitle: "Heavy cotton · Print vorne & hinten",
    badge: "Bestseller",
    placeholderKind: "tshirt",
  },
  {
    slug: "tshirt-heat-power-black",
    category: "shirts",
    eyebrow: "Shirts",
    title: "T-Shirt · Heat & Power · Schwarz",
    subtitle: "Heavy cotton · Gold-Print vorne & hinten",
    placeholderKind: "tshirt",
  },
  {
    slug: "hoodie-heat-power",
    category: "hoodies",
    eyebrow: "Hoodies",
    title: "Hoodie · Heat & Power · Gelb",
    subtitle: "Schwergewicht · Print vorne & hinten",
    badge: "Bestseller",
    placeholderKind: "hoodie",
  },
  {
    slug: "hoodie-heat-power-black",
    category: "hoodies",
    eyebrow: "Hoodies",
    title: "Hoodie · Heat & Power · Schwarz",
    subtitle: "Schwergewicht · Gold-Print · Crew-Klassiker",
    placeholderKind: "hoodie",
  },
  {
    slug: "softshell-jacke",
    category: "jacken",
    eyebrow: "Jacken",
    title: "Softshell · Crew",
    subtitle: "Wind- & wasserabweisend · Heat & Power Patch",
    placeholderKind: "jacket",
  },
  {
    slug: "beanie-power",
    category: "muetzen",
    eyebrow: "Mützen",
    title: "Beanie · Power Icon",
    subtitle: "Strick · gestickter Power-Button gold",
    placeholderKind: "beanie",
  },
  {
    slug: "klappmeter-2m",
    category: "werkzeug",
    eyebrow: "Werkzeug",
    title: "Klappmeter · 2 m",
    subtitle: "Schwarz · gold bedruckt · M22 zugelassen",
    placeholderKind: "ruler",
  },
  {
    slug: "sonnenbrille-crew",
    category: "brillen",
    eyebrow: "Brillen",
    title: "Sonnenbrille · Crew",
    subtitle: "Polarisiert · mit Etui, Beutel & Putztuch",
    placeholderKind: "glasses",
  },
  {
    slug: "notizbuch-a5",
    category: "notes",
    eyebrow: "Notizbücher",
    title: "Worksite Notizbuch · A5",
    subtitle: "Hardcover + Spiralblock · Heat & Power",
    placeholderKind: "notebook",
  },
  {
    slug: "bleistifte-set",
    category: "stifte",
    eyebrow: "Stifte",
    title: "Bleistifte · 4er-Set",
    subtitle: "HB · matt-schwarz · Logo gold",
    placeholderKind: "pencils",
  },
];

const CATEGORY_FILTERS: { key: Category | "all"; labelKey: keyof typeof t.shop.filters | "all" }[] =
  [
    { key: "all", labelKey: "all" },
    { key: "caps", labelKey: "caps" },
    { key: "shirts", labelKey: "shirts" },
    { key: "hoodies", labelKey: "hoodies" },
    { key: "jacken", labelKey: "jacken" },
    { key: "muetzen", labelKey: "muetzen" },
    { key: "werkzeug", labelKey: "werkzeug" },
    { key: "brillen", labelKey: "brillen" },
    { key: "notes", labelKey: "notes" },
    { key: "stifte", labelKey: "stifte" },
  ];

export function ShopRoute() {
  const [filter, setFilter] = useState<Category | "all">("all");
  const [lightbox, setLightbox] = useState<Product | null>(null);

  const visible = useMemo(
    () => (filter === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter)),
    [filter],
  );

  return (
    <div style={{ padding: "24px 32px 64px", maxWidth: 1400, margin: "0 auto" }}>
      <Hero />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          margin: "28px 0 20px",
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CATEGORY_FILTERS.map((f) => {
            const active = filter === f.key;
            const label =
              f.labelKey === "all"
                ? t.shop.filterAll
                : t.shop.filters[f.labelKey as keyof typeof t.shop.filters];
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                style={{
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 500,
                  borderRadius: 999,
                  background: active ? "var(--gold-12)" : "transparent",
                  color: active ? "var(--gold-hi)" : "var(--text-2)",
                  border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <DisabledControl icon={<Ic.Filter size={12} />} label={t.shop.sizeLabel} />
          <DisabledControl icon={<Ic.ChevronDown size={12} />} label={t.shop.sortLabel} trailing />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 16,
        }}
      >
        {visible.map((p) => (
          <ProductCard key={p.slug} product={p} onZoom={() => setLightbox(p)} />
        ))}
      </div>

      <Lightbox product={lightbox} onClose={() => setLightbox(null)} />

      <style>{`
        .product-card .product-thumb {
          image-rendering: -webkit-optimize-contrast;
          will-change: transform;
        }
        .product-card:hover .product-thumb {
          transform: scale(1.04);
        }
        .product-zoom-hint {
          position: absolute;
          right: 10px;
          bottom: 10px;
          width: 28px;
          height: 28px;
          border-radius: 999px;
          background: rgba(11,15,20,0.72);
          backdrop-filter: blur(6px);
          border: 1px solid var(--border);
          color: var(--text-2);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transform: translateY(4px);
          transition: opacity .18s ease, transform .18s ease;
          pointer-events: none;
        }
        .product-card:hover .product-zoom-hint {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}

// ─── Lightbox ──────────────────────────────────────────────────────────────

function Lightbox({ product, onClose }: { product: Product | null; onClose: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <Modal open={!!product} onOpenChange={(o) => !o && onClose()} width={820}>
      {product && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 14,
              gap: 16,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "var(--text-3)",
                  marginBottom: 4,
                }}
              >
                {product.eyebrow}
              </div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{product.title}</h2>
              <div style={{ marginTop: 4, fontSize: 13, color: "var(--text-2)" }}>
                {product.subtitle}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-sm"
              style={{ padding: 6 }}
              aria-label="Schließen"
            >
              <Ic.Close size={16} />
            </button>
          </div>

          <div
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "1 / 1",
              maxHeight: "70vh",
              background: "var(--bg-base)",
              borderRadius: "var(--r-md)",
              border: "1px solid var(--border)",
              overflow: "hidden",
            }}
          >
            {!imgFailed ? (
              <img
                src={`/shop/${product.slug}.png`}
                alt={product.title}
                onError={() => setImgFailed(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <ProductSvgPlaceholder kind={product.placeholderKind} />
            )}
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 12.5,
              color: "var(--text-3)",
            }}
          >
            <span>{t.shop.comingSoonHero}</span>
            <span style={{ color: "var(--gold)", fontWeight: 500 }}>{t.shop.comingSoonBadge}</span>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <div
      className="card"
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "32px 36px",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 320px",
        gap: 24,
        alignItems: "center",
        background:
          "linear-gradient(135deg, var(--bg-card) 0%, var(--bg-base-2) 60%, rgba(201,169,107,0.06) 100%)",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--gold)",
            marginBottom: 12,
          }}
        >
          {t.shop.eyebrow}
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 30,
            lineHeight: 1.15,
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}
        >
          {t.shop.title}
        </h1>
        <p
          style={{
            margin: "12px 0 0",
            color: "var(--text-2)",
            fontSize: 13.5,
            maxWidth: 560,
            lineHeight: 1.55,
          }}
        >
          {t.shop.subtitle}
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 18,
            marginTop: 20,
            fontSize: 12,
            color: "var(--text-3)",
          }}
        >
          <InfoChip icon={<Ic.Truck size={12} />} label={t.shop.info.shipping} />
          <InfoChip icon={<Ic.Check size={12} />} label={t.shop.info.stock} />
          <InfoChip icon={<Ic.Receipt size={12} />} label={t.shop.info.billing} />
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginTop: 22,
            padding: "10px 16px",
            background: "rgba(201,169,107,0.10)",
            border: "1px solid var(--gold)",
            borderRadius: 999,
            fontSize: 12.5,
            color: "var(--gold-hi)",
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: "var(--gold)",
              animation: "portal-pulse 1.6s ease-out infinite",
            }}
          />
          {t.shop.comingSoonHero}
          <span style={{ color: "var(--text-3)", fontWeight: 400 }}>· {t.shop.comingSoonNote}</span>
        </div>
      </div>

      <HeroVisual />
    </div>
  );
}

function HeroVisual() {
  return (
    <div
      style={{
        position: "relative",
        height: 220,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: 10,
      }}
    >
      <HeroTile rotate={-4} slug="cap-classic" alt="Cap" />
      <HeroTile rotate={3} slug="hoodie-heat-power" alt="Hoodie" />
      <HeroTile rotate={2} slug="softshell-jacke" alt="Softshell" />
      <HeroTile rotate={-3} slug="tshirt-heat-power" alt="T-Shirt" />
    </div>
  );
}

function HeroTile({ rotate, slug, alt }: { rotate: number; slug: string; alt: string }) {
  return (
    <div
      style={{
        position: "relative",
        background: "var(--bg-base)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        overflow: "hidden",
        transform: `rotate(${rotate}deg)`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      }}
    >
      <img
        src={`/shop/thumbs/${slug}.png`}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          imageRendering: "-webkit-optimize-contrast",
        }}
      />
    </div>
  );
}

// ─── Product card ──────────────────────────────────────────────────────────

function ProductCard({ product, onZoom }: { product: Product; onZoom: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <div
      className="card product-card"
      style={{
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        opacity: 1,
      }}
    >
      <button
        type="button"
        onClick={onZoom}
        aria-label={`${product.title} vergrößern`}
        style={{
          position: "relative",
          aspectRatio: "1 / 1",
          background: "var(--bg-base)",
          borderBottom: "1px solid var(--border-subtle)",
          overflow: "hidden",
          padding: 0,
          margin: 0,
          width: "100%",
          border: "none",
          cursor: "pointer",
          display: "block",
        }}
      >
        {!imgFailed ? (
          <img
            src={`/shop/thumbs/${product.slug}.png`}
            alt={product.title}
            onError={() => setImgFailed(true)}
            loading="lazy"
            decoding="async"
            className="product-thumb"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              transition: "transform .25s ease",
            }}
          />
        ) : (
          <ProductSvgPlaceholder kind={product.placeholderKind} />
        )}

        <span className="product-zoom-hint" aria-hidden="true">
          <Ic.Search size={14} />
        </span>

        <span
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "4px 8px",
            borderRadius: 999,
            background: "rgba(11,15,20,0.7)",
            backdropFilter: "blur(6px)",
            border: "1px solid var(--border)",
            color: "var(--text-2)",
          }}
        >
          {t.shop.comingSoonBadge}
        </span>

        {product.badge && (
          <span className="badge badge-gold" style={{ position: "absolute", top: 10, right: 10 }}>
            {product.badge}
          </span>
        )}
      </button>

      <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: "var(--text-3)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {product.eyebrow}
        </div>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text)" }}>{product.title}</div>
        <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.4 }}>
          {product.subtitle}
        </div>
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>{t.shop.soldOut}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              color: "var(--gold)",
              textTransform: "uppercase",
            }}
          >
            ●●●
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function InfoChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: "var(--gold)" }}>{icon}</span>
      {label}
    </span>
  );
}

function DisabledControl({
  icon,
  label,
  trailing = false,
}: {
  icon: React.ReactNode;
  label: string;
  trailing?: boolean;
}) {
  return (
    <button
      type="button"
      disabled
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 12px",
        fontSize: 12.5,
        borderRadius: 8,
        background: "var(--bg-base-2)",
        border: "1px solid var(--border)",
        color: "var(--text-3)",
        cursor: "not-allowed",
        opacity: 0.7,
      }}
    >
      {!trailing && icon}
      {label}
      {trailing && icon}
    </button>
  );
}

// ─── SVG placeholders (před nahráním fotek) ────────────────────────────────

function ProductSvgPlaceholder({
  kind,
  compact = false,
}: {
  kind: Product["placeholderKind"];
  compact?: boolean;
}) {
  if (kind === "cap")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(80, 110)">
          {/* crown */}
          <path
            d="M 0 80 Q 0 0 120 0 Q 240 0 240 80 L 240 100 L 0 100 Z"
            fill="#0B0F14"
            stroke="#C9A96B"
            strokeWidth="1.4"
          />
          {/* visor */}
          <path
            d="M -20 100 Q 120 140 260 100 L 240 100 L 0 100 Z"
            fill="#0B0F14"
            stroke="#C9A96B"
            strokeWidth="1.4"
          />
          <text
            x="120"
            y="55"
            fill="#C9A96B"
            fontSize="20"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.14em"
          >
            WARMTEO
          </text>
        </g>
      </BasePlaceholder>
    );

  if (kind === "jacket")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(110, 60)">
          {/* body */}
          <path
            d="M 30 30 L 90 0 L 150 30 L 170 60 L 160 220 L 20 220 L 10 60 Z"
            fill="#0B0F14"
            stroke="#C9A96B"
            strokeWidth="1.4"
          />
          {/* zipper */}
          <line x1="90" y1="20" x2="90" y2="220" stroke="#C9A96B" strokeWidth="1" />
          {/* collar */}
          <path d="M 60 20 L 90 0 L 120 20" fill="none" stroke="#C9A96B" strokeWidth="1.4" />
          <text
            x="90"
            y="80"
            fill="#C9A96B"
            fontSize="11"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.14em"
          >
            WARMTEO
          </text>
          <rect x="125" y="78" width="22" height="22" fill="#C9A96B" />
          <text
            x="136"
            y="93"
            fill="#0B0F14"
            fontSize="6"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
          >
            H&amp;P
          </text>
        </g>
      </BasePlaceholder>
    );

  if (kind === "hoodie")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(90, 50)">
          {/* hoodie body (yellow) */}
          <path
            d="M 30 50 L 90 0 L 150 0 L 210 50 L 200 240 L 40 240 Z"
            fill="#E8B400"
            stroke="#C9A96B"
            strokeWidth="1"
          />
          {/* hood */}
          <path d="M 80 0 Q 120 -10 160 0 L 150 30 L 90 30 Z" fill="#0B0F14" />
          {/* pocket */}
          <rect
            x="80"
            y="160"
            width="80"
            height="40"
            rx="4"
            fill="none"
            stroke="#0B0F14"
            strokeWidth="1"
            opacity="0.4"
          />
          <text
            x="120"
            y="100"
            fill="#0B0F14"
            fontSize="22"
            fontWeight="900"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.02em"
          >
            HEAT
          </text>
          <text
            x="120"
            y="125"
            fill="#0B0F14"
            fontSize="20"
            fontWeight="900"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.02em"
          >
            &amp; POWER
          </text>
          <rect x="80" y="135" width="80" height="16" fill="#0B0F14" />
          <text
            x="120"
            y="147"
            fill="#E8B400"
            fontSize="9"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.14em"
          >
            WARMTEO
          </text>
        </g>
      </BasePlaceholder>
    );

  if (kind === "tshirt")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(80, 70)">
          {/* shirt */}
          <path
            d="M 30 40 L 90 0 L 110 0 Q 120 20 130 0 L 150 0 L 210 40 L 195 80 L 165 70 L 165 220 L 75 220 L 75 70 L 45 80 Z"
            fill="#E8B400"
            stroke="#C9A96B"
            strokeWidth="1"
          />
          <text
            x="120"
            y="120"
            fill="#0B0F14"
            fontSize="22"
            fontWeight="900"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.02em"
          >
            HEAT
          </text>
          <text
            x="120"
            y="145"
            fill="#0B0F14"
            fontSize="20"
            fontWeight="900"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.02em"
          >
            &amp; POWER
          </text>
          <rect x="80" y="155" width="80" height="14" fill="#0B0F14" />
          <text
            x="120"
            y="166"
            fill="#E8B400"
            fontSize="8"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.14em"
          >
            WARMTEO
          </text>
        </g>
      </BasePlaceholder>
    );

  if (kind === "beanie")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(110, 90)">
          {/* beanie */}
          <path
            d="M 0 100 Q 0 0 90 0 Q 180 0 180 100 L 180 130 Q 180 145 165 145 L 15 145 Q 0 145 0 130 Z"
            fill="#0B0F14"
            stroke="#C9A96B"
            strokeWidth="1.4"
          />
          {/* cuff */}
          <rect
            x="0"
            y="105"
            width="180"
            height="40"
            fill="#0B0F14"
            stroke="#C9A96B"
            strokeWidth="1.4"
          />
          {/* power icon (circle with line on top) */}
          <circle cx="90" cy="125" r="13" fill="none" stroke="#C9A96B" strokeWidth="2.5" />
          <line x1="90" y1="110" x2="90" y2="124" stroke="#C9A96B" strokeWidth="2.5" />
          <rect x="86" y="108" width="8" height="3" fill="#0B0F14" />
        </g>
      </BasePlaceholder>
    );

  if (kind === "ruler")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(40, 130) rotate(-12)">
          <rect width="320" height="22" rx="2" fill="#0F1318" stroke="#C9A96B" strokeWidth="1" />
          {Array.from({ length: 16 }).map((_, i) => (
            <line
              key={i}
              x1={20 + i * 18}
              y1="0"
              x2={20 + i * 18}
              y2={i % 5 === 0 ? 11 : 6}
              stroke="#C9A96B"
              strokeWidth="0.8"
            />
          ))}
          <text
            x="160"
            y="16"
            fill="#C9A96B"
            fontSize="9"
            fontWeight="700"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.18em"
          >
            WARMTEO
          </text>
        </g>
        <g transform="translate(60, 200)">
          <rect width="280" height="38" rx="3" fill="#0B0F14" stroke="#C9A96B" strokeWidth="1.4" />
          <text
            x="140"
            y="25"
            fill="#C9A96B"
            fontSize="20"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.1em"
          >
            WARMTEO
          </text>
        </g>
      </BasePlaceholder>
    );

  if (kind === "glasses")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(80, 150)">
          <rect x="0" y="0" width="110" height="60" rx="14" fill="#0B0F14" stroke="#C9A96B" />
          <rect x="130" y="0" width="110" height="60" rx="14" fill="#0B0F14" stroke="#C9A96B" />
          <line x1="110" y1="30" x2="130" y2="30" stroke="#C9A96B" strokeWidth="2" />
          <line x1="-20" y1="20" x2="0" y2="30" stroke="#C9A96B" strokeWidth="2" />
          <line x1="240" y1="30" x2="260" y2="20" stroke="#C9A96B" strokeWidth="2" />
        </g>
        <text
          x="200"
          y="260"
          fill="#C9A96B"
          fontSize="14"
          fontWeight="700"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          letterSpacing="0.18em"
        >
          WARMTEO
        </text>
      </BasePlaceholder>
    );

  if (kind === "notebook")
    return (
      <BasePlaceholder compact={compact}>
        <g transform="translate(70, 80)">
          <rect width="140" height="200" rx="6" fill="#0B0F14" stroke="#C9A96B" strokeWidth="1.5" />
          <text
            x="70"
            y="80"
            fill="#C9A96B"
            fontSize="14"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.06em"
          >
            HEAT &amp;
          </text>
          <text
            x="70"
            y="100"
            fill="#C9A96B"
            fontSize="14"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.06em"
          >
            POWER
          </text>
          <rect x="20" y="115" width="100" height="22" fill="#C9A96B" />
          <text
            x="70"
            y="131"
            fill="#0B0F14"
            fontSize="11"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.14em"
          >
            WARMTEO
          </text>
        </g>
        <g transform="translate(220, 110)">
          <rect width="120" height="170" rx="6" fill="#0B0F14" stroke="#C9A96B" strokeWidth="1.5" />
          <text
            x="60"
            y="92"
            fill="#C9A96B"
            fontSize="13"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.1em"
          >
            WARMTEO
          </text>
        </g>
      </BasePlaceholder>
    );

  // pencils
  return (
    <BasePlaceholder compact={compact}>
      <g transform="translate(60, 110)">
        <rect width="280" height="60" rx="4" fill="#0B0F14" stroke="#C9A96B" />
        <text
          x="140"
          y="36"
          fill="#C9A96B"
          fontSize="18"
          fontWeight="800"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          letterSpacing="0.14em"
        >
          WARMTEO
        </text>
      </g>
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(60, ${190 + i * 14})`}>
          <rect width="280" height="8" fill="#0B0F14" stroke="#C9A96B" strokeWidth="0.6" />
          <text
            x="140"
            y="6.5"
            fill="#C9A96B"
            fontSize="6"
            fontWeight="800"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            letterSpacing="0.1em"
          >
            WARMTEO
          </text>
        </g>
      ))}
    </BasePlaceholder>
  );
}

function BasePlaceholder({
  children,
  compact = false,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 400 320"
      preserveAspectRatio="xMidYMid meet"
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        background:
          "radial-gradient(circle at 30% 30%, rgba(201,169,107,0.08), transparent 60%), #06090D",
      }}
    >
      {children}
      {!compact && (
        <text
          x="200"
          y="305"
          fill="#5C6772"
          fontSize="9"
          fontWeight="500"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          letterSpacing="0.16em"
        >
          PRODUKTFOTO FOLGT
        </text>
      )}
    </svg>
  );
}
